import { useState } from 'react'
import {
  Stack,
  Box,
  Group,
  Text,
  Badge,
  Table,
  Button,
  LoadingOverlay,
  Code,
  Divider,
  ScrollArea,
} from '@mantine/core'
import {
  IconAlertTriangle,
  IconAlertCircle,
  IconInfoCircle,
  IconCheck,
  IconBellRinging2,
} from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'

import { SmallBox, WidgetCard, EmptyState, ResizableDrawer } from '@/components/ui'
import { finopsApi } from '@/api/finops'
import { useCan } from '@/store/auth'
import { severityColor, severityLabel } from '@/utils/finops'
import type { FinopsAlert, FinopsSeverity } from '@/types/finops'

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('pt-BR')
  } catch {
    return iso
  }
}

function alertStatusLabel(alert: FinopsAlert): { label: string; color: string } {
  if (alert.acknowledged_at) return { label: 'ACK', color: 'gray' }
  return { label: 'OPEN', color: 'teal' }
}

export function AlertasV2() {
  const can = useCan()
  const canAck = can('finops', 'ack')
  const queryClient = useQueryClient()

  const [selectedId, setSelectedId] = useState<number | null>(null)

  const { data: alertsResp, isLoading: alertsLoading } = useQuery({
    queryKey: ['finops', 'alerts'],
    queryFn: () => finopsApi.getAlerts({ limit: 100 }),
  })

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['finops', 'alerts', 'summary'],
    queryFn: () => finopsApi.getAlertsSummary(),
  })

  const ackMutation = useMutation({
    mutationFn: (id: number) => finopsApi.ackAlert(id),
    onSuccess: () => {
      notifications.show({
        title: 'Alerta reconhecido',
        message: 'O alerta foi marcado como ACK.',
        color: 'teal',
        icon: <IconCheck size={16} />,
      })
      queryClient.invalidateQueries({ queryKey: ['finops', 'alerts'] })
    },
    onError: () => {
      notifications.show({
        title: 'Falha ao reconhecer',
        message: 'Tente novamente. Se persistir, verifique permissões.',
        color: 'red',
      })
    },
  })

  const items = alertsResp?.items ?? []
  const totalUnacked = summary?.total_unacked ?? items.filter(a => !a.acknowledged_at).length
  const bySev = summary?.by_severity ?? { high: 0, medium: 0, low: 0 }

  const selected = selectedId != null ? items.find(a => a.id === selectedId) ?? null : null

  return (
    <Stack gap="lg">

      {/* KPIs */}
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 16,
        }}
      >
        <SmallBox
          value={totalUnacked}
          label="Não reconhecidos"
          icon={<IconBellRinging2 size={48} stroke={1.5} />}
          color="primary"
          loading={summaryLoading}
          info="Alertas com acknowledged_at IS NULL, somando todos os accounts no escopo."
        />
        <SmallBox
          value={bySev.high}
          label="Severidade Alta"
          icon={<IconAlertTriangle size={48} stroke={1.5} />}
          color="danger"
          loading={summaryLoading}
          info="finops_events.severity = 'high' e não reconhecidos."
        />
        <SmallBox
          value={bySev.medium}
          label="Severidade Média"
          icon={<IconAlertCircle size={48} stroke={1.5} />}
          color="warning"
          loading={summaryLoading}
          info="finops_events.severity = 'medium' e não reconhecidos."
        />
        <SmallBox
          value={bySev.low}
          label="Severidade Baixa"
          icon={<IconInfoCircle size={48} stroke={1.5} />}
          color="info"
          loading={summaryLoading}
          info="finops_events.severity = 'low' e não reconhecidos."
        />
      </Box>

      {/* Tabela */}
      <WidgetCard accent="primary" p={0}>
        <Box style={{ position: 'relative', minHeight: 160 }}>
          <LoadingOverlay visible={alertsLoading} zIndex={2} />

          {!alertsLoading && items.length === 0 ? (
            <EmptyState
              icon={IconBellRinging2}
              title="Nenhum alerta FinOps"
              description="Quando a EventBridge entregar eventos críticos (anomalias de custo, mudança de Reserved Instance, etc.), eles aparecerão aqui."
              accent="success"
              size="md"
              bare
            />
          ) : (
            <Table striped highlightOnHover withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Severidade</Table.Th>
                  <Table.Th>Fonte</Table.Th>
                  <Table.Th>Evento</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Data</Table.Th>
                  <Table.Th>Account</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map(alert => {
                  const sev = alert.severity as FinopsSeverity
                  const status = alertStatusLabel(alert)
                  return (
                    <Table.Tr
                      key={alert.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelectedId(alert.id)}
                    >
                      <Table.Td>
                        <Badge color={severityColor(sev)} variant="filled" size="sm">
                          {severityLabel(sev)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="var(--ai-text-secondary)">
                          {alert.event_source}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {alert.event_name}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={status.color} variant="light" size="sm">
                          {status.label}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="var(--ai-text-muted)">
                          {formatDateTime(alert.event_time)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="var(--ai-text-muted)">
                          {alert.aws_account_id ?? '—'}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
          )}
        </Box>
      </WidgetCard>

      {/* Drawer detalhe */}
      <ResizableDrawer
        storageKey="finops-alert-drawer"
        defaultWidth={520}
        minWidth={420}
        opened={selected != null}
        onClose={() => setSelectedId(null)}
        position="right"
        withCloseButton
        title={
          selected ? (
            <Group gap="xs">
              <Badge color={severityColor(selected.severity)} variant="filled">
                {severityLabel(selected.severity)}
              </Badge>
              <Text size="sm" fw={600}>
                {selected.event_name}
              </Text>
            </Group>
          ) : (
            'Detalhe'
          )
        }
      >
        {selected && (
          <Stack gap="md">
            <Box>
              <Text size="xs" c="dimmed" mb={4}>
                Fonte
              </Text>
              <Text size="sm">{selected.event_source}</Text>
            </Box>

            <Group grow>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>
                  Account
                </Text>
                <Text size="sm">{selected.aws_account_id ?? '—'}</Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>
                  Região
                </Text>
                <Text size="sm">{selected.aws_region ?? '—'}</Text>
              </Box>
            </Group>

            <Box>
              <Text size="xs" c="dimmed" mb={4}>
                Principal
              </Text>
              <Text size="sm">
                {selected.principal_arn ?? '—'}
                {selected.principal_type && (
                  <Text component="span" c="dimmed" ml="xs" size="xs">
                    ({selected.principal_type})
                  </Text>
                )}
              </Text>
            </Box>

            <Group grow>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>
                  Data do evento
                </Text>
                <Text size="sm">{formatDateTime(selected.event_time)}</Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>
                  Recebido em
                </Text>
                <Text size="sm">{formatDateTime(selected.received_at)}</Text>
              </Box>
            </Group>

            {selected.acknowledged_at && (
              <Box>
                <Text size="xs" c="dimmed" mb={4}>
                  Reconhecido em
                </Text>
                <Text size="sm">
                  {formatDateTime(selected.acknowledged_at)}
                  {selected.acknowledged_by != null && (
                    <Text component="span" c="dimmed" ml="xs" size="xs">
                      (user #{selected.acknowledged_by})
                    </Text>
                  )}
                </Text>
              </Box>
            )}

            <Divider />

            <Box>
              <Text size="xs" c="dimmed" mb={4}>
                Payload bruto
              </Text>
              <ScrollArea h={260} type="auto">
                <Code block style={{ fontSize: 11, lineHeight: 1.5 }}>
                  {JSON.stringify(selected.payload, null, 2)}
                </Code>
              </ScrollArea>
            </Box>

            {!selected.acknowledged_at && canAck && (
              <Group justify="flex-end" mt="sm">
                <Button
                  color="teal"
                  leftSection={<IconCheck size={16} />}
                  loading={ackMutation.isPending}
                  onClick={() => ackMutation.mutate(selected.id)}
                >
                  Reconhecer alerta
                </Button>
              </Group>
            )}
          </Stack>
        )}
      </ResizableDrawer>
    </Stack>
  )
}
